<template>
  <div class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5)">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Start New Household Visit</h5>
          <button type="button" class="btn-close" @click="$emit('close')"></button>
        </div>
        <div class="modal-body">
          <!-- Step 1: Select Household -->
          <div v-if="step === 1">
            <div class="mb-4">
              <h6 class="mb-3">Select Household to Visit</h6>

              <!-- Search -->
              <div class="search-box mb-3">
                <div class="position-relative">
                  <input v-model="searchQuery" @input="searchHouseholds" type="text"
                         class="form-control" placeholder="Search household number or head name...">
                  <i class="bx bx-search-alt search-icon"></i>
                </div>
              </div>

              <!-- Recent/Scheduled Households -->
              <div v-if="scheduledHouseholds.length > 0" class="mb-4">
                <h6 class="text-muted mb-2">Scheduled for Today</h6>
                <div class="list-group">
                  <div v-for="household in scheduledHouseholds" :key="household.household_id"
                       class="list-group-item list-group-item-action"
                       :class="{'active': selectedHousehold?.household_id === household.household_id}"
                       @click="selectHousehold(household)">
                    <div class="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 class="mb-1">{{ household.household_number }}</h6>
                        <p class="mb-0 text-muted">{{ household.household_head_name }}</p>
                      </div>
                      <span class="badge bg-warning">Scheduled</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Search Results -->
              <div v-if="searchResults.length > 0">
                <h6 class="text-muted mb-2">Search Results</h6>
                <div class="list-group">
                  <div v-for="household in searchResults" :key="household.household_id"
                       class="list-group-item list-group-item-action"
                       :class="{'active': selectedHousehold?.household_id === household.household_id}"
                       @click="selectHousehold(household)">
                    <div class="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 class="mb-1">{{ household.household_number }}</h6>
                        <p class="mb-0 text-muted">
                          {{ household.household_head_name }}
                          <span v-if="household.last_visit_date" class="ms-2">
                            • Last visit: {{ formatDate(household.last_visit_date) }}
                          </span>
                        </p>
                      </div>
                      <div>
                        <span v-if="household.cbhi_member_count > 0" class="badge bg-success me-1">CBHI</span>
                        <span v-if="household.is_model_household" class="badge bg-warning">Model</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- No Results -->
              <div v-else-if="searchQuery && !loading" class="text-center py-4">
                <p class="text-muted">No households found</p>
              </div>
            </div>
          </div>

          <!-- Step 2: Visit Details -->
          <div v-else-if="step === 2">
            <div class="mb-4">
              <h6 class="mb-3">Visit Details</h6>

              <!-- Selected Household Summary -->
              <div class="alert alert-info">
                <h6 class="alert-heading">{{ selectedHousehold.household_number }}</h6>
                <p class="mb-0">
                  Head: {{ selectedHousehold.household_head_name || 'Not assigned' }}<br>
                  Members: {{ selectedHousehold.member_count || 0 }}
                  <span v-if="selectedHousehold.cbhi_member_count > 0" class="ms-2">
                    ({{ selectedHousehold.cbhi_member_count }} CBHI)
                  </span>
                </p>
              </div>

              <!-- Visit Type -->
              <div class="mb-3">
                <label class="form-label">Visit Type <span class="text-danger">*</span></label>
                <select v-model="visitForm.visitType" class="form-select" required>
                  <option value="">Select visit type</option>
                  <option value="routine">Routine Health Promotion</option>
                  <option value="follow_up">Follow-up Visit</option>
                  <option value="emergency">Emergency/Urgent</option>
                  <option value="campaign">Campaign Activity</option>
                  <option value="assessment">Environmental Assessment</option>
                </select>
              </div>

              <!-- Visit Purpose -->
              <div class="mb-3">
                <label class="form-label">Visit Purpose</label>
                <textarea v-model="visitForm.visitPurpose" class="form-control" rows="2"
                          placeholder="Brief description of visit purpose..."></textarea>
              </div>

              <!-- GPS Location -->
              <div class="mb-3">
                <div class="form-check">
                  <input v-model="captureGPS" class="form-check-input" type="checkbox" id="captureGPSCheck">
                  <label class="form-check-label" for="captureGPSCheck">
                    Capture GPS Location
                  </label>
                </div>
                <div v-if="gpsStatus" class="mt-2">
                  <span v-if="gpsLoading" class="text-primary">
                    <i class="bx bx-loader-alt bx-spin"></i> Getting location...
                  </span>
                  <span v-else-if="gpsError" class="text-danger">
                    <i class="bx bx-error-circle"></i> {{ gpsError }}
                  </span>
                  <span v-else class="text-success">
                    <i class="bx bx-check-circle"></i> Location captured
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="text-center py-4">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="$emit('close')">Cancel</button>

          <button v-if="step === 1" type="button" class="btn btn-primary"
                  @click="nextStep" :disabled="!selectedHousehold">
            Next <i class="bx bx-chevron-right"></i>
          </button>

          <div v-else-if="step === 2">
            <button type="button" class="btn btn-secondary me-2" @click="previousStep">
              <i class="bx bx-chevron-left"></i> Back
            </button>
            <button type="button" class="btn btn-success" @click="startVisit"
                    :disabled="!visitForm.visitType || submitting">
              <span v-if="submitting">
                <span class="spinner-border spinner-border-sm me-1"></span> Starting...
              </span>
              <span v-else>
                <i class="bx bx-check"></i> Start Visit
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, executeJson } from "@/api";

export default {
  name: "NewVisitModal",
  data() {
    return {
      step: 1,
      loading: false,
      submitting: false,

      // Household selection
      searchQuery: '',
      searchResults: [],
      scheduledHouseholds: [],
      selectedHousehold: null,

      // Visit form
      visitForm: {
        visitType: '',
        visitPurpose: ''
      },

      // GPS
      captureGPS: false,
      gpsLoading: false,
      gpsError: null,
      gpsStatus: false,
      gpsCoordinates: {
        latitude: null,
        longitude: null
      }
    };
  },

  watch: {
    captureGPS(value) {
      if (value) {
        this.getGPSLocation();
      } else {
        this.gpsCoordinates = { latitude: null, longitude: null };
        this.gpsStatus = false;
      }
    }
  },

  mounted() {
    this.loadScheduledHouseholds();
  },

  methods: {
    async loadScheduledHouseholds() {
      this.loading = true;

      const data = {
        "function": "getWorkSchedule",
        "date": new Date().toISOString().split('T')[0],
        "view": "day"
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          // Filter scheduled households that haven't been visited
          this.scheduledHouseholds = (res.data.data.schedule || [])
              .filter(s => s.household_id && s.status === 'pending')
              .map(s => ({
                household_id: s.household_id,
                household_number: s.household_number,
                household_head_name: s.household_head,
                scheduled_time: s.scheduled_time,
                activity_description: s.activity_description
              }));
        }
      } catch (e) {
        console.error("Failed to load scheduled households:", e);
      } finally {
        this.loading = false;
      }
    },

    async searchHouseholds() {
      if (!this.searchQuery || this.searchQuery.length < 2) {
        this.searchResults = [];
        return;
      }

      this.loading = true;

      const data = {
        "function": "getHouseholds",
        "searchTerm": this.searchQuery,
        "limit": 10,
        "offset": 0
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.searchResults = res.data.data.households || [];
        }
      } catch (e) {
        console.error("Failed to search households:", e);
      } finally {
        this.loading = false;
      }
    },

    selectHousehold(household) {
      this.selectedHousehold = household;
    },

    nextStep() {
      if (!this.selectedHousehold) return;
      this.step = 2;
    },

    previousStep() {
      this.step = 1;
    },

    async getGPSLocation() {
      if (!navigator.geolocation) {
        this.gpsError = "Geolocation is not supported by your browser";
        return;
      }

      this.gpsLoading = true;
      this.gpsError = null;
      this.gpsStatus = true;

      navigator.geolocation.getCurrentPosition(
          (position) => {
            this.gpsCoordinates = {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude
            };
            this.gpsLoading = false;
          },
          (error) => {
            this.gpsLoading = false;
            switch(error.code) {
              case error.PERMISSION_DENIED:
                this.gpsError = "Location permission denied";
                break;
              case error.POSITION_UNAVAILABLE:
                this.gpsError = "Location information unavailable";
                break;
              case error.TIMEOUT:
                this.gpsError = "Location request timed out";
                break;
              default:
                this.gpsError = "An unknown error occurred";
            }
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0
          }
      );
    },

    async startVisit() {
      if (!this.visitForm.visitType || !this.selectedHousehold) return;

      this.submitting = true;

      const data = {
        "function": "createHouseholdVisit",
        "householdId": this.selectedHousehold.household_id,
        "visitType": this.visitForm.visitType,
        "visitPurpose": this.visitForm.visitPurpose,
        "gpsLatitude": this.gpsCoordinates.latitude,
        "gpsLongitude": this.gpsCoordinates.longitude
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.$emit('started', {
            visitId: res.data.data.visitId,
            householdId: res.data.data.householdId
          });
        } else {
          alert(res.data.message || 'Failed to start visit');
        }
      } catch (e) {
        console.error("Failed to start visit:", e);
        alert('Failed to start visit. Please try again.');
      } finally {
        this.submitting = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
  }
};
</script>

<style scoped>
.modal {
  display: block;
}

.search-icon {
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  color: #999;
}

.list-group-item {
  cursor: pointer;
}

.list-group-item:hover {
  background-color: #f8f9fa;
}

.list-group-item.active {
  background-color: #556ee6;
  border-color: #556ee6;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.15em;
}

.bx-spin {
  animation: spin 2s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>